import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Stack, Box, Button, DialogActions, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import { BookingTypeSubmitRules } from '../../../../utils/globalEnums';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import IntacctConnectionForm from './ConnectionForms/IntacctConnectionForm';
import QuickbooksConnectionForm from './ConnectionForms/QuickbooksConnectionForm';

BookingTypeConnectionForm.propTypes = {
  bookingType: PropTypes.object,
  close: PropTypes.func,
};

export default function BookingTypeConnectionForm({ bookingType, close }) {

  const [formValue, setFormValue] = useState({ id: bookingType?.id });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitalAdd, setIsInitalAdd] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (bookingType) {
      let urlSegment = null;
      if (bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughIntacct) urlSegment = "BookingTypeIntacctConnection";
      if (urlSegment) {
        setLoading(true);
        axiosInstance.get(`Admin/InvoiceBookingType/${urlSegment}?id=${bookingType.id}`)
          .then(res => {
            setIsInitalAdd(!res.data.companyId && !res.data.userId);
            setFormValue(form => ({ ...form, ...(res.data ?? {}) }))
          })
          .catch(() => enqueueSnackbar("Failed to load booking data", { variant: "error" }))
          .finally(() => setLoading(false));
      }
    }
  }, [bookingType]);

  const handleTestConnection = (forceReconnect) => {
    setSaving(true);
    axiosInstance.post(`Admin/InvoiceBookingType/TestBookingTypeConnection`, objectToFormData(forceReconnect ? { ...formValue, forceReconnect } : formValue))
      .then(res => {
        if (res.data.authURL) window.open(res.data.authURL, "_blank");
        else if (res.data.connectionResults?.length) {
          setResults(res.data.connectionResults);
          const failed = res.data.connectionResults.some(conRes => !conRes.success);
          enqueueSnackbar(failed ? "Test failed" : "Test succeeded", { variant: failed ? "error" : "success" });
        }
        else enqueueSnackbar("Test failed", { variant: "error" });
      })
      .catch(() => enqueueSnackbar("Test failed", { variant: "error" }))
      .finally(() => setSaving(false));
  };

  const handleFetchData = () => {
    if (bookingType) {
      setSaving(true);
      axiosInstance.get(`Admin/InvoiceBookingType/FetchBookingTypeData?id=${bookingType.id}`)
        .then(() => enqueueSnackbar("Data is being fetched... You'll receive an email once done", { variant: "success" }))
        .catch(() => enqueueSnackbar("Failed to fetch data", { variant: "error" }))
        .finally(() => { setTimeout(() => setSaving(false), 400) });
    }
  };

  return <Dialog open onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    close();
  }} maxWidth="md" fullWidth>
    <DialogTitle sx={{ textAlign: 'center' }} >
      Connect Booking Account ({bookingType?.submitType})
      <CloseIconButton onClick={close} />
    </DialogTitle>
    <DialogContent sx={{ height: '100%', width: '100%' }} dividers>
      {loading ? <LoadingAnimation /> : <Box sx={{ width: '100%' }}>
        <Stack spacing={1}>
          {bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughIntacct && <IntacctConnectionForm formValue={formValue} isInitalAdd={isInitalAdd} setFormValue={setFormValue} />}
          {bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks && <QuickbooksConnectionForm />}
          {results?.length > 0 && <Stack spacing={1}>
            {results.map((res, index) => <Alert sx={{ width: '100%', height: '35px', fontSize: 12, py: 0, px: 1, m: 0 }} key={index}
              severity={res.success ? "success" : (!res.important || res.message === "Success but no results") ? "warning" : "error"}>
              {res.type} - {res.message}
            </Alert>)}
            {bookingType.connectionError && <Alert sx={{ width: '100%', height: '35px', fontSize: 12, py: 0, px: 1, m: 0 }} severity={"error"}>
              Login - {bookingType.connectionError}
            </Alert>}
            {bookingType.connectionSuccess && <Alert sx={{ width: '100%', height: '35px', fontSize: 12, py: 0, px: 1, m: 0 }} severity={"success"}>
              Login - Success
            </Alert>}
          </Stack>}
        </Stack>
      </Box>}
    </DialogContent>
    <DialogActions>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" color="error" size="medium" sx={{ width: 150 }} onClick={close}>
          Cancel
        </Button>
        {bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks && <LoadingButton
          variant="contained"
          color="primary"
          size="medium"
          sx={{ width: 150 }}
          onClick={handleFetchData}
          loading={saving}
        >
          Fetch Data
        </LoadingButton>}
        {bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks && <LoadingButton
          variant="contained"
          color="primary"
          size="medium"
          sx={{ width: 150 }}
          onClick={() => handleTestConnection(true)}
          loading={saving}
        >
          Connect
        </LoadingButton>}
        <LoadingButton
          variant="contained"
          color="primary"
          size="medium"
          sx={{ width: 150 }}
          onClick={() => handleTestConnection(false)}
          loading={saving}
        >
          Test Connection
        </LoadingButton>
      </Stack>
    </DialogActions>
  </Dialog>;
}
